import React, { useState, useEffect, useRef } from "react";
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Sparkles, ArrowRight, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";

const RULESETS = [
  { value: "all", label: "All rulesets (FCA + ASA + ICO)" },
  { value: "fca", label: "FCA Financial Promotions" },
  { value: "asa", label: "ASA Advertising Standards" },
  { value: "ico", label: "ICO / UK GDPR" },
];

const PLACEHOLDER = `e.g. "Earn guaranteed 8% returns on your savings — capital risk-free. Open an ISA today with our award-winning team. Limited time offer."`;

function ScoreGauge({ score = 0, verdict = "pass" }) {
  const [drawn, setDrawn] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDrawn(score), 80);
    return () => clearTimeout(t);
  }, [score]);

  const radius = 70;
  const circ = 2 * Math.PI * radius;
  const dashOffset = circ - (drawn / 100) * circ;

  const color = verdict === "pass" ? "#10B981" : verdict === "review" ? "#F59E0B" : "#EF4444";

  return (
    <div className="relative" style={{ width: 180, height: 180 }} data-testid="score-gauge">
      <svg viewBox="0 0 180 180" width="180" height="180">
        <circle cx="90" cy="90" r={radius} fill="none" strokeWidth="12" className="score-track" />
        <circle
          cx="90" cy="90" r={radius} fill="none" strokeWidth="12"
          stroke={color} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={dashOffset}
          transform="rotate(-90 90 90)"
          className="score-arc"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="t-mono" style={{ fontSize: 44, fontWeight: 700, color: "#0F1C2E" }}>{Math.round(drawn)}</span>
        <span className="t-caption">out of 100</span>
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  if (verdict === "pass") return <span className="badge2 badge-verified" data-testid="verdict-pass"><CheckCircle2 size={12} />Pass</span>;
  if (verdict === "review") return <span className="badge2 badge-pending" data-testid="verdict-review"><AlertTriangle size={12} />Review required</span>;
  return <span className="badge2 badge-failed" data-testid="verdict-fail"><XCircle size={12} />Fail — do not publish</span>;
}

function SeverityChip({ severity }) {
  const map = {
    info: { cls: "badge-locked", label: "Info" },
    warning: { cls: "badge-pending", label: "Warning" },
    critical: { cls: "badge-failed", label: "Critical" },
  };
  const x = map[severity] || map.warning;
  return <span className={`badge2 ${x.cls}`}>{x.label}</span>;
}

export default function ScannerWidget() {
  const [text, setText] = useState("");
  const [ruleset, setRuleset] = useState("all");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const resultsRef = useRef(null);

  const onScan = async () => {
    if (!text.trim()) {
      setError("Paste some text to check first.");
      return;
    }
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/api/compliance/scan/public", { text, ruleset });
      setResult(data);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card2 p-6 md:p-8" data-testid="scanner-widget">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={18} className="text-indigo2" />
        <span className="t-label" style={{ color: "#4F46E5" }}>Free compliance scanner</span>
      </div>
      <h3 className="t-h2 mb-1">Paste any copy. Get a verdict in seconds.</h3>
      <p className="t-body text-slate2 mb-6">Real-time FCA-aware compliance check. No signup needed. 5 free checks per hour.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="md:col-span-2">
          <label className="label2">Marketing copy or client communication</label>
          <textarea
            className="textarea2"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={8}
            maxLength={5000}
            data-testid="scanner-textarea"
          />
          <div className="help2 flex justify-between"><span>Max 5,000 characters</span><span>{text.length}/5000</span></div>
        </div>
        <div>
          <label className="label2">Check against</label>
          <select
            className="input2"
            value={ruleset}
            onChange={(e) => setRuleset(e.target.value)}
            data-testid="scanner-ruleset"
          >
            {RULESETS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button
            className="btn btn-primary btn-block mt-3"
            onClick={onScan}
            disabled={loading}
            data-testid="scanner-submit"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" />Analysing…</> : <>Check my text <ArrowRight size={16} /></>}
          </button>
          <div className="mt-3 flex items-center gap-1.5 t-caption">
            <Lock size={12} className="text-emerald2-dark" />
            Your text is not stored. IP-rate limited only.
          </div>
        </div>
      </div>

      {error && <div className="mt-4 err2" data-testid="scanner-error">{error}</div>}

      {result && (
        <div ref={resultsRef} className="mt-8 border-t pt-8" data-testid="scanner-results">
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-center">
            <ScoreGauge score={result.score} verdict={result.verdict} />
            <div>
              <div className="flex items-center gap-3 mb-2">
                <VerdictBadge verdict={result.verdict} />
                <span className="t-caption t-mono">{result.flagged_items.length} flagged · {result.passed_checks?.length || 0} passed</span>
              </div>
              <p className="t-body-lg text-ink">{result.summary}</p>
            </div>
          </div>

          {result.flagged_items.length > 0 && (
            <div className="mt-8">
              <div className="t-label mb-3 text-slate2">Flagged items</div>
              <div className="space-y-3">
                {result.flagged_items.map((it, i) => (
                  <div
                    key={i}
                    className="rounded-lg border p-4"
                    style={{
                      background: it.severity === "critical" ? "rgba(239,68,68,0.04)" : it.severity === "warning" ? "rgba(245,158,11,0.04)" : "rgba(79,70,229,0.04)",
                      borderColor: "#E2E8F0",
                      borderLeftWidth: 3,
                      borderLeftColor: it.severity === "critical" ? "#EF4444" : it.severity === "warning" ? "#F59E0B" : "#4F46E5",
                    }}
                    data-testid={`flagged-item-${i}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <SeverityChip severity={it.severity} />
                      <span className="t-mono t-caption" style={{ color: "#0F1C2E", fontWeight: 600 }}>{it.rule_reference}</span>
                    </div>
                    <div className="t-body-sm mb-2 italic" style={{ color: "#475569" }}>"{it.excerpt}"</div>
                    <div className="t-body-sm mb-2">{it.explanation}</div>
                    <div className="t-body-sm" style={{ color: "#065F46" }}>
                      <strong>Suggested fix:</strong> {it.suggested_fix}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.passed_checks?.length > 0 && (
            <div className="mt-6">
              <div className="t-label mb-3 text-slate2">Passed checks</div>
              <ul className="space-y-1.5">
                {result.passed_checks.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 t-body-sm">
                    <CheckCircle2 size={16} className="text-emerald2 mt-0.5 flex-shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 p-5 rounded-lg" style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #ECFDF5 100%)" }}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="t-h4">Save this report and run unlimited checks</div>
                <p className="t-body-sm text-slate2 mt-1">Get the full audit trail, suggested rewrites, and PDF reports with a free 14-day trial.</p>
              </div>
              <Link to="/signup" className="btn btn-primary" data-testid="scanner-cta-signup">Start free trial <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
